import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import UsersContext from 'contexts/UsersContext';
import AccountPickerContext from 'contexts/AccountPickerContext';
import useLockedDebouncedEffect from 'hooks/useLockedDebouncedEffect';
import isDefined from 'isDefined';
import STORE from 'store';

interface GetMappedUsersArgs {
  signedUpAndPendingUsers: (
    | UserManagement.SignedUpUser
    | UserManagement.PendingUser
  )[];
  entityId: string;
}

/**
 * This is used for both V2 and V3 mapped users
 */
const useMappedUsers = ({
  entityId,
  entityAppId,
}: {
  entityId: string;
  /**
   * This argument allows further filtering of mapped users to be just those
   * against this app
   */
  entityAppId?: string;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { signedUpAndPendingUsers, isLoading: isLoadingUsers } =
    useContext(UsersContext);
  const [mappedUsers, setMappedUsers] = useState<MappedUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const getMappedUsers = useCallback(
    async (args: GetMappedUsersArgs): Promise<MappedUser[]> => {
      setIsLoading(true);
      const { signedUpAndPendingUsers, entityId } = args;

      const promises = signedUpAndPendingUsers
        .filter((u) => !u.isDeleted)
        .map(async (user): Promise<undefined | MappedUser> => {
          const userContentSettings = await STORE.userManagement
            .getUserContentSettingsRef({
              accountId: selectedAccountId,
              userId: user.id,
            })
            .get();

          if (!userContentSettings.exists) {
            return undefined;
          }
          const settings = userContentSettings.data();
          if (!settings) {
            return undefined;
          }
          if (settings.mode === 'normal') {
            return undefined;
          }

          const mapping = settings.mappings.find((m) => {
            if (entityAppId === undefined) {
              return m.entityId === entityId;
            }

            return m.entityId === entityId && m.entityAppId === entityAppId;
          });
          if (!mapping) {
            return undefined;
          }

          return {
            filterValue: mapping.primaryFieldValue,
            user,
            additionalFilters: mapping.additionalFilters || [],
            accessToOtherMappedUserIds: mapping.accessToOtherMappedUserIds,
            entityId: mapping.entityId,
            entityAppId: mapping.entityAppId,
          };
        });

      const results = await Promise.all(promises);
      return results.filter(isDefined);
    },
    [entityAppId, selectedAccountId],
  );

  const args = useMemo(() => {
    if (isLoadingUsers) {
      return undefined;
    }

    return {
      signedUpAndPendingUsers,
      entityId,
    };
  }, [signedUpAndPendingUsers, entityId, isLoadingUsers]);

  const handleResponse = useCallback((response: MappedUser[]) => {
    setMappedUsers(response);
    setIsLoading(false);
  }, []);

  /**
   * We need to trigger a loading state immediately here to prevent a janky
   * when a mapped user switches between their mapped entities
   */
  useEffect(() => {
    setIsLoading(true);
  }, [args]);

  useLockedDebouncedEffect({
    args,
    responseHandler: handleResponse,
    callback: getMappedUsers,
  });

  return { mappedUsers, setMappedUsers, isLoading };
};

export default useMappedUsers;
